import { EnvironmentProviders, inject, InjectionToken, makeEnvironmentProviders, Type } from '@angular/core';

/**
 * Helper function to retrieve the version of a component from localStorage or return a default version if not found.
 * @param storageKey The key used to store the component version in localStorage
 * @param defaultVersion The default version to use if no version is found in localStorage
 * @param useLocalStorage Whether to check localStorage at all; when false, defaultVersion is always returned
 * @returns The version number of the component
 */
export function componentVersioningFn(storageKey: string, defaultVersion: number, useLocalStorage: boolean = false): number {
  if (useLocalStorage) {
    const storedVersion = localStorage.getItem(storageKey);
    if (storedVersion !== null) {
      const version = Number(storedVersion);
      return Number.isNaN(version) ? defaultVersion : version;
    }
  }

  return defaultVersion; // Return default version if not found or parsing fails
}

export interface ComponentVersions {
  [componentName: string]: ReturnType<typeof componentVersioningFn>; // Maps component names to their version numbers
}

export const COMPONENT_VERSIONS = new InjectionToken<ComponentVersions>('ComponentVersions');

/**
 * Generic environment provider that substitutes a versioned implementation for a base service token,
 * chosen by the service's configured version (from `COMPONENT_VERSIONS`). Add once in the app's root
 * providers, alongside `provideAppConfig`.
 * @param componentName The key into `COMPONENT_VERSIONS` to read the version for (e.g. 'marketplace')
 * @param baseToken The token other code injects — a class, or an `InjectionToken` when the versions
 *   share an interface rather than a base class (e.g. `MARKETPLACE_SOURCE`)
 * @param versionToImplementation Maps a version number to the class to resolve for that version
 * @param defaultImplementation Used when the configured version has no entry; required when
 *   `baseToken` is an `InjectionToken`, which cannot itself be constructed
 * @example
 * providers: [
 *   provideAppConfig({ componentVersions: { marketplace: 2, lotto: 1 } }),
 *   provideVersionedService('marketplace', MARKETPLACE_SOURCE, { 1: MarketplaceV1Source, 2: MarketplaceV2Source }, MarketplaceV1Source),
 *   provideVersionedService('lotto', LOTTO_SOURCE, { 1: LottoV1Source, 2: LottoV2Source }, LottoV1Source),
 * ]
 */
export function provideVersionedService<T>(
  componentName: string,
  baseToken: Type<T> | InjectionToken<T>,
  versionToImplementation: Record<number, Type<T>>,
  defaultImplementation?: Type<T>
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: baseToken,
      useFactory: () => {
        const version = inject(COMPONENT_VERSIONS)[componentName];
        const implementation = versionToImplementation[version] ?? defaultImplementation ?? (baseToken as Type<T>);
        // Resolved through the injector rather than `new`: the implementation may declare
        // constructor dependencies, and this keeps it a singleton rather than building a fresh
        // instance that bypasses DI. It also lets an InjectionToken be the base token, so a
        // versioned service need not be a class its consumers inject directly.
        return inject(implementation);
      },
    },
  ]);
}
